import {Dispatch, SetStateAction, ReactNode, FC} from 'react';

import {PageFilter} from 'filters/types';
import {Falsy, SVGIcon} from 'types';
import {BasicTreeDataOptions, UseTreeData, TreeData} from 'modules/decisionsNext/hooks/trees/types';

import {YupSchema} from 'utils/yup';

import {TreeBasicNode, TreeKey} from 'components/Tree/TreeTypes';
import {FilterTreePickerProps} from 'components/TreePicker/FilterTreePicker';

export enum FilterDialogSectionId {
  ActiveFilters = 'activeFilters',
  RecentFilters = 'recentFilters',
  SavedFilters = 'savedFilters',
  DateAndType = 'dateAndType',
  ActivityCodes = 'activityCodes',
  Departments = 'departments',
  Carriers = 'carriers',
  Markets = 'markets',
  States = 'states',
  Products = 'products',
  Employees = 'employees',
  Industries = 'industries',
  Teams = 'teams',
  ClientTags = 'clientTags',
  PolicyTags = 'policyTags',
  AgencyCodes = 'agencyCodes',
  SettingsUsers = 'settingsUsers',
  SettingsRoles = 'settingsRoles',
  SettingsPermissions = 'settingsPermissions',
  SettingsUserEnvironments = 'settingsUserEnvironments',
}

export type SectionBasicFields = {
  id: FilterDialogSectionId;
  title: string;
  icon?: SVGIcon;
  withDivider?: boolean;
  // Collapsible by default
  collapsibleActiveFilterSection?: boolean;
};

export type ActiveFilterChip = {
  id: string;
  disabled?: boolean;
  label: ReactNode;
  onClick?: () => void;
  onDelete?: () => void;
};

// FIXME: refactor to avoid noisy Falsy
export type ActiveFilterSectionContent = Array<
  Falsy<{
    id: string;
    label?: string;
    disabled?: boolean;
    chips: Falsy<ActiveFilterChip>[];
  }>
>;

export type BasicTreeViewSelectionOptions = {
  disabled?: boolean;
};

export type TreeViewSelection<
  K extends TreeKey = string,
  Node extends TreeBasicNode<K> = TreeBasicNode<K>,
  V = string[] | null,
  O extends BasicTreeViewSelectionOptions = BasicTreeViewSelectionOptions
> = (
  treeData: TreeData<K, Node>,
  // setValue doesn't support batching in one callback
  field: {value: V; setValue: (value: V) => void},
  options: O
) => Omit<FilterTreePickerProps<K, TreeBasicNode<K>>, 'treeData' | 'isLoading' | 'entity'> & {
  overrideActiveFilterContent?: (context: {sectionId: FilterDialogSectionId}) => ActiveFilterSectionContent;
  // For internal use only
  isAllSelected?: boolean;
};

export type TreeViewSectionConfig<
  V,
  K extends TreeKey,
  Node extends TreeBasicNode<K>,
  TDOptions extends BasicTreeDataOptions,
  TSOptions extends BasicTreeViewSelectionOptions
> = SectionBasicFields & {
  type: 'tree-view';
  // title by default
  entity?: string;
  validate?: YupSchema;
  getValue: (filter: PageFilter) => V;
  onChange: (filter: PageFilter, value: V) => PageFilter;
  useTreeData: UseTreeData<K, Node, TDOptions>;
  useTreeSelection: TreeViewSelection<K, Node, V, TSOptions>;
  getDataOptions?: (value: V) => TDOptions;
  selectionOptions?: TSOptions;
  footer?: {
    primaryAction: FC<Omit<TreeViewSectionConfig<V, K, Node, TDOptions, TSOptions>, 'footer'>>;
    secondaryAction?: FC<Omit<TreeViewSectionConfig<V, K, Node, TDOptions, TSOptions>, 'footer'>>;
    additionalActions?: (
      section: Omit<TreeViewSectionConfig<V, K, Node, TDOptions, TSOptions>, 'footer'>
    ) => ReactNode[];
  };
};

export enum FilterDialogItemId {
  DateType = 'dateType',
  PremiumType = 'premiumType',
  RenewalsDateRange = 'renewalsDateRange',
  DateRange = 'dateRange',
  DatasourceType = 'datasourceType',
  PolicyStatus = 'policyStatus',
  Markets = 'markets',
}

export enum FilterDialogItemType {
  RadioGroup = 'radio-group',
}

type FilterDialogItemConfig<V = any, T = {}> = {
  id: FilterDialogItemId;
  label: string;
  getValue: (filter: PageFilter) => V;
  onChange: (filter: PageFilter, value: V) => PageFilter;
  getActiveFiltersContent: (
    field: {value: V; setValue: (value: V) => void},
    context: {
      sectionId: FilterDialogSectionId;
    }
  ) => ActiveFilterSectionContent[number];
  validate?: YupSchema;
} & T;

export type RadioGroupItemConfig<T> = FilterDialogItemConfig<
  T,
  {
    options: {
      label: string;
      value: T;
      annotation?: string;
    }[];
  }
>;

export type CustomDialogItem<V> = {
  id: FilterDialogItemId;
  Component: FC<
    Omit<CustomDialogItem<V>, 'Component' | 'getActiveFiltersContent' | 'id'> & {
      id: string;
    }
  >;
  getActiveFiltersContent: (
    field: {value: V; setValue: Dispatch<SetStateAction<V>>},
    context: {
      sectionId: FilterDialogSectionId;
    }
  ) => ActiveFilterSectionContent[number];
  getValue: (filter: PageFilter) => V;
  onChange: (filter: PageFilter, value: V) => PageFilter;
  validate?: YupSchema;
};

export type ItemsSectionConfig = SectionBasicFields & {
  type: 'items';
  overrideActiveFilterContent?: (
    filter: PageFilter,
    setFilter: Dispatch<SetStateAction<PageFilter>>
  ) => ActiveFilterSectionContent | null;
  items: Array<
    | (RadioGroupItemConfig<any> & {
        type: FilterDialogItemType.RadioGroup;
      })
    | CustomDialogItem<any>
  >;
  footer?: {
    primaryAction: FC<Omit<ItemsSectionConfig, 'footer'>>;
    secondaryAction?: FC<Omit<ItemsSectionConfig, 'footer'>>;
    additionalActions?: (props: Omit<ItemsSectionConfig, 'footer'>) => ReactNode[];
  };
};

export type ActiveFiltersSectionConfig<PrependProps = {}, AppendProps = {}> = SectionBasicFields & {
  type: 'active-filters';
  prependContent?: FC<PrependProps>;
  prependProps?: PrependProps;
  appendContent?: FC<AppendProps>;
  appendProps?: AppendProps;
  footer?: {
    primaryAction: FC<Omit<ActiveFiltersSectionConfig, 'footer'>>;
    secondaryAction?: FC<Omit<ActiveFiltersSectionConfig, 'footer'>>;
    additionalActions?: (props: Omit<ActiveFiltersSectionConfig, 'footer'>) => ReactNode[];
  };
};

export type SavedFiltersSectionConfig = SectionBasicFields & {
  type: 'saved-filters';
  savedId: string | null;
  setSavedId: (id: string | null) => void;
  footer?: {
    primaryAction: FC<Omit<SavedFiltersSectionConfig, 'footer'>>;
    secondaryAction?: FC<Omit<SavedFiltersSectionConfig, 'footer'>>;
    additionalActions?: (props: Omit<SavedFiltersSectionConfig, 'footer'>) => ReactNode[];
  };
};

export type RecentFiltersSectionConfig = SectionBasicFields & {
  type: 'recent-filters';
  recentId: string | null;
  setRecentId: (id: string | null) => void;
  footer?: {
    primaryAction: FC<Omit<RecentFiltersSectionConfig, 'footer'>>;
    secondaryAction?: FC<Omit<RecentFiltersSectionConfig, 'footer'>>;
    additionalActions?: (props: Omit<RecentFiltersSectionConfig, 'footer'>) => ReactNode[];
  };
};

export type FilterDialogSection =
  | TreeViewSectionConfig<any, any, any, any, any>
  | ItemsSectionConfig
  | ActiveFiltersSectionConfig<any, any>
  | SavedFiltersSectionConfig
  | RecentFiltersSectionConfig;
